import React, { useEffect, useState } from "react";
import axios from "axios";
import { Store } from "react-notifications-component";
import { Camera, RotateCcw, Trash2 } from "lucide-react";
import setAuthToken from "../../utils/setAuthToken";
import confirmAlert from "../../utils/confirmAlert";

import {
  Card,
  Input,
  Button,
  FormField,
  IconButton,
  Tooltip,
  Spinner,
  EmptyState,
} from "../../design-system";

/**
 * Panneau de gestion des snapshots d'un article (sidebar admin).
 * Liste, création, restauration, suppression via /api/articles/:id/snapshots/...
 *
 * Props :
 *   - articleId (string, required)
 *   - onRestored (function, optional)
 */
const ArticleSnapshotsPanel = ({ articleId, onRestored }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");

  const ensureAuth = () => {
    const token = localStorage.getItem("jwtToken");
    if (token) setAuthToken(token);
  };

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "0 o";
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ko`;
    return `${(bytes / 1024 / 1024).toFixed(2)} Mo`;
  };

  const notify = (type, title, message) => {
    Store.addNotification({
      title,
      message,
      type,
      insert: "top",
      container: "top-right",
      dismiss: { duration: 3000 },
    });
  };

  const load = async () => {
    if (!articleId) return;
    ensureAuth();
    try {
      const res = await axios.get(`/api/articles/${articleId}/snapshots`);
      setSnapshots(res.data.snapshots || []);
    } catch (e) {
      notify("danger", "Erreur", e.response?.data?.message || "Impossible de charger les snapshots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  const create = async () => {
    if (!articleId) return;
    ensureAuth();
    setCreating(true);
    try {
      const res = await axios.post(`/api/articles/${articleId}/snapshots`, {
        label: label.trim() || null,
      });
      setSnapshots([res.data.snapshot, ...snapshots]);
      setLabel("");
      notify("success", "Snapshot créé", "Le contenu actuel est sauvegardé.");
    } catch (e) {
      notify("danger", "Erreur", e.response?.data?.message || "Création échouée");
    } finally {
      setCreating(false);
    }
  };

  const restore = (snapshot) => {
    confirmAlert({
      title: "Restaurer ce snapshot ?",
      message: `Le contenu actuel sera remplacé par celui du snapshot du ${formatDate(snapshot.createdAt)}. Cette opération est irréversible — pense à prendre un snapshot de l'état actuel d'abord.`,
      type: "warning",
      confirmText: "Restaurer",
      cancelText: "Annuler",
      onConfirm: async () => {
        ensureAuth();
        try {
          await axios.post(`/api/articles/${articleId}/snapshots/${snapshot.id}/restore`);
          notify("success", "Restauré", "L'article a été restauré.");
          if (onRestored) onRestored();
        } catch (e) {
          notify("danger", "Erreur", e.response?.data?.message || "Restauration échouée");
        }
      },
    });
  };

  const remove = (snapshot) => {
    confirmAlert({
      title: "Supprimer ce snapshot ?",
      message: `Snapshot du ${formatDate(snapshot.createdAt)} ${snapshot.label ? `(« ${snapshot.label} »)` : ""}. Cette action est irréversible.`,
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      onConfirm: async () => {
        ensureAuth();
        try {
          await axios.delete(`/api/articles/${articleId}/snapshots/${snapshot.id}`);
          setSnapshots(snapshots.filter((s) => s.id !== snapshot.id));
          notify("success", "Supprimé", "Snapshot supprimé.");
        } catch (e) {
          notify("danger", "Erreur", e.response?.data?.message || "Suppression échouée");
        }
      },
    });
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title className="inline-flex items-center gap-2">
          <Camera className="w-4 h-4 text-mute" strokeWidth={1.8} />
          Snapshots
        </Card.Title>
      </Card.Header>

      <div className="space-y-3">
        <FormField label="Label" optional helper="ex. « avant refonte »">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Nommer ce snapshot…"
            maxLength={200}
          />
        </FormField>

        <Button
          variant="secondary"
          fullWidth
          isLoading={creating}
          leftIcon={<Camera className="w-3.5 h-3.5" strokeWidth={1.8} />}
          onClick={create}
        >
          {creating ? "Création…" : "Prendre un snapshot"}
        </Button>

        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Spinner size="sm" />
          </div>
        ) : snapshots.length === 0 ? (
          <p className="text-[12px] text-mute italic text-center py-2">
            Aucun snapshot pour le moment.
          </p>
        ) : (
          <ul className="divide-y divide-border -mx-4">
            {snapshots.map((s) => (
              <li key={s.id} className="px-4 py-2.5 flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11.5px] text-text leading-tight">
                    {formatDate(s.createdAt)}
                  </div>
                  {s.label && (
                    <div className="font-serif italic text-[12px] text-text-2 mt-0.5 line-clamp-1">
                      « {s.label} »
                    </div>
                  )}
                  <div className="text-[10.5px] font-mono uppercase tracking-wider text-mute mt-1">
                    {formatSize(s.size)}
                    {s.author?.name && <span> · {s.author.name}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Tooltip content="Restaurer">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<RotateCcw strokeWidth={1.8} />}
                      aria-label="Restaurer ce snapshot"
                      onClick={() => restore(s)}
                    />
                  </Tooltip>
                  <Tooltip content="Supprimer">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<Trash2 strokeWidth={1.8} />}
                      aria-label="Supprimer ce snapshot"
                      onClick={() => remove(s)}
                    />
                  </Tooltip>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
};

export default ArticleSnapshotsPanel;
